import {
  apiErrorSchema,
  artifactListSchema,
  atcInputListSchema,
  atcStandardSchema,
  batchOpenedSchema,
  bootstrapSchema,
  buildMaturitySchema,
  codeRequestResultSchema,
  createMemberResultSchema,
  currentUserSchema,
  derivedListSchema,
  execAtcListSchema,
  healthSchema,
  homeNumbersSchema,
  statCardListSchema,
  statDetailSchema,
  ingestItemListSchema,
  ingestResultSchema,
  inviteTargetSchema,
  ledgerPageSchema,
  mailDeliverySchema,
  mailProbeResultSchema,
  memberListSchema,
  memberSchema,
  nodeTreeSchema,
  nodeTypeSchema,
  okSchema,
  organizationSchema,
  atcVersionListSchema,
  atcStandardListSchema,
  atcPersonListSchema,
  createAtcVersionResultSchema,
  poolCardListSchema,
  proposedCardSchema,
  poolSummarySchema,
  runLedgerSchema,
  sessionProbeSchema,
  timelinePageSchema,
  versionDurationListSchema,
  departmentListSchema,
  departmentSchema,
  personLedgerSchema,
  personLedgerRowSchema,
  atcListSchema,
  atcRowSchema,
  positionListSchema,
  positionWithReadinessSchema,
  type Bootstrap,
  type CodeRequestResult,
  type CreateMember,
  type CreateMemberResult,
  type CreateDepartmentInput,
  type CreatePositionInput,
  type Department,
  type PersonLedger,
  type PersonLedgerRow,
  type CreatePersonInput,
  type UpdatePersonInput,
  type ReassignPersonInput,
  type DepartPersonInput,
  type PositionWithReadiness,
  type AtcRow,
  type BuildMaturity,
  type CreateAtcInput,
  type RenameNodeType,
  type SetHeadcountInput,
  type UpdateAtcInput,
  type UpdateDepartmentInput,
  type UpdatePositionInput,
  type Artifact,
  type AtcInput,
  type AtcStandard,
  type CurrentUser,
  type DerivedRecord,
  type ExecAtcSummary,
  type Health,
  type HomeNumbers,
  type StatCardList,
  type StatDetail,
  type IngestItem,
  type IngestResult,
  type MailDelivery,
  type MailProbeResult,
  type LedgerPage,
  type Member,
  type NodeTree,
  type NodeType,
  type Organization,
  type AtcVersionList,
  type AtcStandardList,
  type AtcPersonList,
  type CreateAtcVersion,
  type CreateAtcVersionResult,
  type PoolCardList,
  type ProposedCard,
  type PoolSummary,
  type RunLedgerRow,
  type Provenance,
  type SaveMailDelivery,
  type TimeAxis,
  type TimelinePage,
  type UpdateOrganization,
  type VersionDurationList,
} from '@capsio/contracts';

/** 只要求「能把 unknown 校成 T」，这样调用处不必关心 schema 是什么类型。 */
type Parser<T> = { parse: (value: unknown) => T };

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function readError(response: Response): Promise<ApiRequestError> {
  try {
    const parsed = apiErrorSchema.parse(await response.json());
    return new ApiRequestError(response.status, parsed.error.code, parsed.error.message);
  } catch {
    return new ApiRequestError(response.status, 'unexpected_response', `HTTP ${response.status}`);
  }
}

async function request<T>(
  path: string,
  schema: Parser<T>,
  init?: { method?: string; body?: unknown; headers?: Record<string, string> },
): Promise<T> {
  const response = await fetch(path, {
    method: init?.method ?? 'GET',
    // 登录态走 httpOnly cookie，必须带上。
    credentials: 'include',
    headers: {
      ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (!response.ok) throw await readError(response);
  return schema.parse(await response.json());
}

export const api = {
  health: (): Promise<Health> => request('/api/health', healthSchema),

  /** 首屏五数（ADR-0047）。三格有数，两格无源——**无源那两格拿到的是 null 加一句原因**。 */
  homeNumbers: (): Promise<HomeNumbers> => request('/api/home/numbers', homeNumbersSchema),

  /**
   * 运营统计的卡片墙（C-168 A5）。
   *
   * `node` 是站在树上的哪个节点，**层级由服务端从节点自己是什么推出来**——
   * 前端不传层级，传了就有两处判断，一旦不一致，界面会在部门节点下列出企业级表。
   */
  statTables: (node: string | null): Promise<StatCardList> =>
    request(
      node === null ? '/api/stats/tables' : `/api/stats/tables?node=${encodeURIComponent(node)}`,
      statCardListSchema,
    ),

  /** 一张统计表的详情七段。 */
  statTable: (code: string, node: string | null): Promise<StatDetail> =>
    request(
      `/api/stats/tables/${encodeURIComponent(code)}` +
        (node === null ? '' : `?node=${encodeURIComponent(node)}`),
      statDetailSchema,
    ),

  /** 待建卡清单。**待建与在役分开**——待建不进达成率与替代率的分母（F 段六）。 */
  poolCards: (): Promise<PoolCardList> => request('/api/pool/cards', poolCardListSchema),

  /** 版本历史。**没有它，「旧版不覆盖」在界面上看不出来**，人就会想办法原地改。 */
  atcVersions: (code: string): Promise<AtcVersionList> =>
    request(`/api/atc/${encodeURIComponent(code)}/versions`, atcVersionListSchema),

  /** 这张卡当前版本登记的标准（standard_ref 经 atc_standard 挂版本）。空 = 未登记（零态）。 */
  atcStandards: (code: string): Promise<AtcStandardList> =>
    request(`/api/atc/${encodeURIComponent(code)}/standards`, atcStandardListSchema),

  /** 这张卡上的人员台账（三角色在册名单 + 视图计数）。卡页「人员与责任」分区的数据源。 */
  atcCardPeople: (code: string): Promise<AtcPersonList> =>
    request(`/api/atc/${encodeURIComponent(code)}/people`, atcPersonListSchema),

  /** 提一版新定义。**只有 POST**——定义仅追加，改不是一种操作。 */
  addAtcVersion: (code: string, body: CreateAtcVersion): Promise<CreateAtcVersionResult> =>
    request(`/api/atc/${encodeURIComponent(code)}/versions`, createAtcVersionResultSchema, {
      method: 'POST',
      body,
    }),

  poolCard: (code: string): Promise<ProposedCard> =>
    request(`/api/pool/cards/${encodeURIComponent(code)}`, proposedCardSchema),

  bootstrap: (): Promise<Bootstrap> => request('/api/bootstrap', bootstrapSchema),

  session: (): Promise<{ user: CurrentUser | null }> => request('/api/session', sessionProbeSchema),

  /**
   * 初始化。**必须带初始化令牌**（MF-04）。
   *
   * 未初始化实例上的这个接口是「谁先到谁当管理员」，所以服务端要求
   * `X-Bootstrap-Token`——令牌由服务端启动时写到 `DATA_DIR/bootstrap-token`（0600），
   * 由能登上那台机器的人取出来。
   *
   * 上一版这里**没有传这个头**，于是界面上永远初始化不了，只能用 curl。
   * 那不是「运维就该用命令行」，是这一步的界面缺了一格输入框——
   * 查出来的经过见 C-088 一：界面走不通的那一步，就是要补的功能。
   */
  setup: (body: unknown, bootstrapToken: string): Promise<CurrentUser> =>
    request('/api/setup', currentUserSchema, {
      method: 'POST',
      body,
      headers: { 'x-bootstrap-token': bootstrapToken },
    }),

  login: (email: string, password: string): Promise<CurrentUser> =>
    request('/api/auth/login', currentUserSchema, { method: 'POST', body: { email, password } }),

  requestLoginCode: (email: string): Promise<CodeRequestResult> =>
    request('/api/auth/code/request', codeRequestResultSchema, { method: 'POST', body: { email } }),

  verifyLoginCode: (email: string, code: string): Promise<CurrentUser> =>
    request('/api/auth/code/verify', currentUserSchema, { method: 'POST', body: { email, code } }),

  requestPasswordReset: (email: string): Promise<CodeRequestResult> =>
    request('/api/auth/password-reset/request', codeRequestResultSchema, {
      method: 'POST',
      body: { email },
    }),

  completePasswordReset: (email: string, code: string, password: string): Promise<CurrentUser> =>
    request('/api/auth/password-reset/complete', currentUserSchema, {
      method: 'POST',
      body: { email, code, password },
    }),

  changePassword: (currentPassword: string, newPassword: string): Promise<CurrentUser> =>
    request('/api/auth/password', currentUserSchema, {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),

  logout: (): Promise<{ ok: true }> => request('/api/auth/logout', okSchema, { method: 'POST' }),

  readInvite: (token: string): Promise<{ email: string; name: string }> =>
    request(`/api/invite/${encodeURIComponent(token)}`, inviteTargetSchema),

  acceptInvite: (token: string, password: string): Promise<CurrentUser> =>
    request('/api/invite/accept', currentUserSchema, { method: 'POST', body: { token, password } }),

  members: (): Promise<{ members: Member[] }> => request('/api/members', memberListSchema),

  addMember: (body: CreateMember): Promise<CreateMemberResult> =>
    request('/api/members', createMemberResultSchema, { method: 'POST', body }),

  resetMemberPassword: (id: string, password: string): Promise<Member> =>
    request(`/api/members/${encodeURIComponent(id)}/reset-password`, memberSchema, {
      method: 'POST',
      body: { password },
    }),

  deleteMember: (id: string): Promise<{ ok: true }> =>
    request(`/api/members/${encodeURIComponent(id)}`, okSchema, { method: 'DELETE' }),

  tree: (): Promise<NodeTree> => request('/api/tree', nodeTreeSchema),

  /**
   * 组织架构的写路径。**直写 department / position 两张领域表。**
   *
   * 旧的 `/api/nodes*` 已全部退役（410）——那套写的是 `Node` 树，
   * 而 ATC 挂的是 `position.position_id`，客户录完组织架构卡挂不上去
   * （HUB-ARC-001 断桥、C-077）。树现在是这两张表的投影，读还在 `/api/tree`。
   */
  departments: (): Promise<{ rows: Department[] }> =>
    request('/api/org/departments', departmentListSchema),

  addDepartment: (body: CreateDepartmentInput): Promise<Department> =>
    request('/api/org/departments', departmentSchema, { method: 'POST', body }),

  updateDepartment: (id: string, body: UpdateDepartmentInput): Promise<Department> =>
    request(`/api/org/departments/${encodeURIComponent(id)}`, departmentSchema, {
      method: 'PATCH',
      body,
    }),

  positions: (): Promise<{ rows: PositionWithReadiness[] }> =>
    request('/api/org/positions', positionListSchema),

  addPosition: (body: CreatePositionInput): Promise<PositionWithReadiness> =>
    request('/api/org/positions', positionWithReadinessSchema, { method: 'POST', body }),

  updatePosition: (id: string, body: UpdatePositionInput): Promise<PositionWithReadiness> =>
    request(`/api/org/positions/${encodeURIComponent(id)}`, positionWithReadinessSchema, {
      method: 'PATCH',
      body,
    }),

  /**
   * 设定岗位编制。**AI 替代率分母的唯一入口**（MF-02）。
   *
   * 用 PUT 不用 PATCH：它设定的是「当前编制」这个整体状态，
   * 服务端把旧区间收口、追加新区间——不是就地改一个字段。
   */
  setHeadcount: (id: string, body: SetHeadcountInput): Promise<PositionWithReadiness> =>
    request(`/api/org/positions/${encodeURIComponent(id)}/headcount`, positionWithReadinessSchema, {
      method: 'PUT',
      body,
    }),

  /** 上线。四项没齐会被库拒，错误原话直接显示给人看——界面不复制那个判断。 */
  activatePosition: (id: string): Promise<PositionWithReadiness> =>
    request(`/api/org/positions/${encodeURIComponent(id)}/activate`, positionWithReadinessSchema, {
      method: 'POST',
    }),

  atcs: (): Promise<{ rows: AtcRow[] }> => request('/api/org/atcs', atcListSchema),

  /** 建设成熟度：左栏行尾「N 卡 · 百分比/未调研」的数（派生视图，不落存储列）。 */
  orgBuildMaturity: (): Promise<BuildMaturity> =>
    request('/api/org/build-maturity', buildMaturitySchema),

  /**
   * 人员台账（第四张台账，C-169 A5）。
   *
   * **不是 `/api/atc/:code/people`**——那个问的是「这张卡上有谁」。
   * 这个问的是「企业里有哪些人、各自在哪个岗位」，一个人可以在岗而一张卡都没挂。
   */
  people: (): Promise<PersonLedger> => request('/api/org/people', personLedgerSchema),

  /* ───── 人员台账写路径（C-181 A4/A5）：新增 / 修改 / 调岗 / 离职 ─────
   *
   * 工号是锚点，不出现在任何入参里。没有「人员归档」「人员恢复」——
   * 人员只有在职与离职。
   */
  addPerson: (body: CreatePersonInput): Promise<PersonLedgerRow> =>
    request('/api/org/people', personLedgerRowSchema, { method: 'POST', body }),

  updatePerson: (employeeNo: string, body: UpdatePersonInput): Promise<PersonLedgerRow> =>
    request(`/api/org/people/${encodeURIComponent(employeeNo)}`, personLedgerRowSchema, {
      method: 'PATCH',
      body,
    }),

  /** 调岗：闭合当前区间 + 追加新区间，服务端一个事务。 */
  reassignPerson: (employeeNo: string, body: ReassignPersonInput): Promise<PersonLedgerRow> =>
    request(
      `/api/org/people/${encodeURIComponent(employeeNo)}/assignment`,
      personLedgerRowSchema,
      { method: 'POST', body },
    ),

  /** 离职。一次性动作——`employed_to` 写下就不可再改。 */
  departPerson: (employeeNo: string, body: DepartPersonInput): Promise<PersonLedgerRow> =>
    request(
      `/api/org/people/${encodeURIComponent(employeeNo)}/departure`,
      personLedgerRowSchema,
      { method: 'POST', body },
    ),

  /* ───── 状态迁移：三张表各一个，都落到服务端同一族入口（C-165 A3） ─────
   *
   * `POST .../status` 而不是 `PATCH { status }`：状态迁移带前置条件与留痕，
   * 与改名不是一类。`reason` 在停用 / 归档时服务端会要求非空。
   */
  changeDepartmentStatus: (id: string, status: string, reason = ''): Promise<Department> =>
    request(`/api/org/departments/${encodeURIComponent(id)}/status`, departmentSchema, {
      method: 'POST',
      body: { status, reason },
    }),

  changePositionStatus: (
    id: string,
    status: string,
    reason = '',
  ): Promise<PositionWithReadiness> =>
    request(`/api/org/positions/${encodeURIComponent(id)}/status`, positionWithReadinessSchema, {
      method: 'POST',
      body: { status, reason },
    }),

  changeAtcStatus: (id: string, status: string, reason = ''): Promise<AtcRow> =>
    request(`/api/org/atcs/${encodeURIComponent(id)}/status`, atcRowSchema, {
      method: 'POST',
      body: { status, reason },
    }),

  /** 建卡。**编码不传**——它要印到设备标签与二维码上，由服务端按规则生成（ADR-0021）。 */
  addAtc: (body: CreateAtcInput): Promise<AtcRow> =>
    request('/api/org/atcs', atcRowSchema, { method: 'POST', body }),

  updateAtc: (id: string, body: UpdateAtcInput): Promise<AtcRow> =>
    request(`/api/org/atcs/${encodeURIComponent(id)}`, atcRowSchema, { method: 'PATCH', body }),

  /** 层的叫法。层级定义权已随 ADR-0020 六退役，这里只剩改名。 */
  renameNodeType: (code: string, body: RenameNodeType): Promise<NodeType> =>
    request(`/api/node-types/${encodeURIComponent(code)}`, nodeTypeSchema, {
      method: 'PATCH',
      body,
    }),

  organization: (): Promise<Organization> =>
    request('/api/settings/organization', organizationSchema),

  saveOrganization: (body: UpdateOrganization): Promise<Organization> =>
    request('/api/settings/organization', organizationSchema, { method: 'PATCH', body }),

  mailSettings: (): Promise<MailDelivery> => request('/api/settings/mail', mailDeliverySchema),

  probeMail: (domain: string): Promise<MailProbeResult> =>
    request('/api/settings/mail/probe', mailProbeResultSchema, {
      method: 'POST',
      body: { domain },
    }),

  saveMail: (body: SaveMailDelivery): Promise<MailDelivery> =>
    request('/api/settings/mail', mailDeliverySchema, { method: 'POST', body }),

  clearMail: (): Promise<MailDelivery> =>
    request('/api/settings/mail', mailDeliverySchema, { method: 'DELETE' }),

  /* ───── ATC 执行页（D 段） ───── */

  execAtcs: (): Promise<{ rows: ExecAtcSummary[] }> => request('/api/exec/atcs', execAtcListSchema),

  execStandard: (code: string): Promise<AtcStandard> =>
    request(`/api/exec/atcs/${encodeURIComponent(code)}`, atcStandardSchema),

  execInputs: (code: string): Promise<{ rows: AtcInput[] }> =>
    request(`/api/exec/atcs/${encodeURIComponent(code)}/inputs`, atcInputListSchema),

  execRuns: (code: string): Promise<{ rows: RunLedgerRow[] }> =>
    request(`/api/exec/atcs/${encodeURIComponent(code)}/runs`, runLedgerSchema),

  /** 逐版实测均耗（C-191）。卡页「基线对照」块的分子——降幅由库算（vsBaseline），不在界面上除。 */
  execVersionDurations: (code: string): Promise<VersionDurationList> =>
    request(
      `/api/exec/atcs/${encodeURIComponent(code)}/version-durations`,
      versionDurationListSchema,
    ),

  execArtifacts: (runRef: string): Promise<{ rows: Artifact[] }> =>
    request(`/api/exec/runs/${encodeURIComponent(runRef)}/artifacts`, artifactListSchema),

  ingestSummary: (): Promise<PoolSummary> => request('/api/ingest/summary', poolSummarySchema),

  openIngestBatch: (body: {
    label?: string;
    provenance: Provenance;
    /** 执行页上传时带上它，入库顺手登记为那张卡的输入资料（登记不是归属，I-5）。 */
    atcCode?: string;
  }): Promise<{ batchId: string }> =>
    request('/api/ingest/batches', batchOpenedSchema, { method: 'POST', body }),

  ingestTimeline: (params: {
    axis: TimeAxis;
    q?: string;
    carrier?: string[];
    provenance?: string;
    batch?: string;
    customer?: string;
    from?: string;
    to?: string;
    includeDerived?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<TimelinePage> => {
    const search = new URLSearchParams();
    search.set('axis', params.axis);
    if (params.q !== undefined && params.q !== '') search.set('q', params.q);
    if (params.carrier !== undefined && params.carrier.length > 0) {
      search.set('carrier', params.carrier.join(','));
    }
    if (params.provenance !== undefined) search.set('provenance', params.provenance);
    if (params.batch !== undefined) search.set('batch', params.batch);
    if (params.customer !== undefined && params.customer !== '')
      search.set('customer', params.customer);
    if (params.from !== undefined) search.set('from', params.from);
    if (params.to !== undefined) search.set('to', params.to);
    if (params.includeDerived === true) search.set('includeDerived', 'true');
    search.set('limit', String(params.limit ?? 50));
    search.set('offset', String(params.offset ?? 0));
    return request(`/api/ingest/timeline?${search.toString()}`, timelinePageSchema);
  },

  ingestDerived: (materialId: string): Promise<{ rows: DerivedRecord[] }> =>
    request(`/api/ingest/materials/${encodeURIComponent(materialId)}/derived`, derivedListSchema),

  ingestLedger: (limit = 30, offset = 0): Promise<LedgerPage> =>
    request(`/api/ingest/ledger?limit=${limit}&offset=${offset}`, ledgerPageSchema),

  ingestBatchItems: (batchId: string): Promise<{ rows: IngestItem[] }> =>
    request(`/api/ingest/batches/${encodeURIComponent(batchId)}/items`, ingestItemListSchema),
};

/**
 * 上传一份文件到某一批。
 *
 * **一份一请求**，不用 multipart：一次一份才有逐份进度与逐份结果，
 * 而批量上传里最要紧的信息恰恰是「哪一份失败了」。文件名等元数据走请求头——
 * HTTP 头是 latin-1，中文文件名必须先 `encodeURIComponent`，否则到不了服务端。
 */
export async function uploadIngestFile(
  batchId: string,
  file: File,
  provenance: Provenance,
): Promise<IngestResult> {
  const headers: Record<string, string> = {
    'content-type': 'application/octet-stream',
    'x-file-name': encodeURIComponent(file.name),
    'x-provenance': provenance,
  };
  // 拖整个文件夹进来时浏览器给相对路径。客户侧目录结构本身是信息，有就带上。
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (relative !== undefined && relative !== '') {
    headers['x-file-path'] = encodeURIComponent(relative);
  }
  // 判定「资料本身的时间」时最弱的一档兜底。
  if (file.lastModified > 0) headers['x-file-mtime'] = String(file.lastModified);

  const response = await fetch(`/api/ingest/batches/${encodeURIComponent(batchId)}/files`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: file,
  });
  if (!response.ok) throw await readError(response);
  return ingestResultSchema.parse(await response.json());
}

/** 产出物本体的取用地址。产出库与资料库分开（ADR-0032 二）。 */
export function artifactContentUrl(artifactId: string): string {
  return `/api/exec/artifacts/${encodeURIComponent(artifactId)}/content`;
}

/** 本体的取用地址。预览与下载共用——内容哈希寻址，可以长缓存。 */
export function materialContentUrl(materialId: string): string {
  return `/api/ingest/materials/${encodeURIComponent(materialId)}/content`;
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return '未知错误';
}
